// Componentes para renderizar campos tipo tabla en FormularioEditor.jsx

import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

/**
 * Componente para tabla dinámica (comportamientos)
 */
export const TablaDinamica = ({ campo, value = [], onChange }) => {
  const { configuracion } = campo;
  const [filas, setFilas] = useState(value.length > 0 ? value : [{}]);

  useEffect(() => {
    // Inicializar con al menos una fila vacía
    if (!value || value.length === 0) {
      const filaVacia = {};
      configuracion.columnas.forEach(col => {
        filaVacia[col.id] = '';
      });
      setFilas([filaVacia]);
    } else {
      setFilas(value);
    }
  }, [value, configuracion.columnas]);

  const handleCellChange = (rowIndex, columnId, newValue) => {
    const nuevasFilas = [...filas];
    if (!nuevasFilas[rowIndex]) {
      nuevasFilas[rowIndex] = {};
    }
    nuevasFilas[rowIndex][columnId] = newValue;
    setFilas(nuevasFilas);
    onChange(nuevasFilas);
  };

  const agregarFila = () => {
    if (configuracion.max_filas && filas.length >= configuracion.max_filas) {
      return;
    }
    const nuevaFila = {};
    configuracion.columnas.forEach(col => {
      nuevaFila[col.id] = '';
    });
    const nuevasFilas = [...filas, nuevaFila];
    setFilas(nuevasFilas);
    onChange(nuevasFilas);
  };

  const eliminarFila = (index) => {
    if (filas.length <= (configuracion.min_filas || 1)) {
      return;
    }
    const nuevasFilas = filas.filter((_, i) => i !== index);
    setFilas(nuevasFilas);
    onChange(nuevasFilas);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {configuracion.columnas.map((columna) => (
                <TableCell 
                  key={columna.id} 
                  style={{ width: columna.ancho }}
                  sx={{ fontWeight: 'bold' }}
                >
                  {columna.etiqueta}
                  {columna.requerido && <span style={{ color: 'red' }}> *</span>}
                </TableCell>
              ))}
              {configuracion.permitir_eliminar && (
                <TableCell width="50px" align="center">
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((fila, rowIndex) => (
              <TableRow key={rowIndex}>
                {configuracion.columnas.map((columna) => (
                  <TableCell key={columna.id}>
                    {columna.tipo === 'texto_largo' ? (
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={columna.placeholder}
                        value={fila[columna.id] || ''}
                        onChange={(e) => handleCellChange(rowIndex, columna.id, e.target.value)}
                        required={columna.requerido}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder={columna.placeholder}
                        value={fila[columna.id] || ''}
                        onChange={(e) => handleCellChange(rowIndex, columna.id, e.target.value)}
                        required={columna.requerido}
                      />
                    )}
                  </TableCell>
                ))}
                {configuracion.permitir_eliminar && (
                  <TableCell align="center">
                    <Tooltip title="Eliminar fila">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => eliminarFila(rowIndex)}
                        disabled={filas.length <= (configuracion.min_filas || 1)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {configuracion.permitir_agregar && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={agregarFila}
            disabled={configuracion.max_filas && filas.length >= configuracion.max_filas}
            size="small"
          >
            {configuracion.texto_agregar || 'Agregar fila'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Componente para tabla de evaluación (áreas cognitivas)
 */
export const TablaEvaluacion = ({ campo, value = {}, onChange }) => {
  const { configuracion } = campo;
  const [evaluaciones, setEvaluaciones] = useState(value || {});

  const handleCellChange = (areaId, columnaId, newValue) => {
    const nuevasEvaluaciones = {
      ...evaluaciones,
      [areaId]: {
        ...evaluaciones[areaId],
        [columnaId]: newValue
      }
    };
    setEvaluaciones(nuevasEvaluaciones);
    onChange(nuevasEvaluaciones);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {configuracion.columnas.map((columna) => (
                <TableCell 
                  key={columna.id} 
                  style={{ width: columna.ancho }}
                  sx={{ fontWeight: 'bold' }}
                >
                  {columna.etiqueta}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {configuracion.areas.map((grupo) => (
              <React.Fragment key={grupo.grupo}>
                {/* Fila de grupo */}
                <TableRow>
                  <TableCell 
                    colSpan={configuracion.columnas.length} 
                    sx={{ 
                      backgroundColor: '#f5f5f5', 
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    {grupo.grupo}
                  </TableCell>
                </TableRow>
                
                {/* Filas de items */}
                {grupo.items.map((item) => (
                  <TableRow key={item.id}>
                    {configuracion.columnas.map((columna) => (
                      <TableCell key={columna.id}>
                        {columna.solo_lectura ? (
                          <Typography variant="body2" sx={{ pl: 2 }}>
                            {item.nombre}
                          </Typography>
                        ) : columna.tipo === 'texto_largo' ? (
                          <TextField
                            multiline
                            rows={2}
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder={columna.placeholder}
                            value={evaluaciones[item.id]?.[columna.id] || ''}
                            onChange={(e) => handleCellChange(item.id, columna.id, e.target.value)}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder={columna.placeholder}
                            value={evaluaciones[item.id]?.[columna.id] || ''}
                            onChange={(e) => handleCellChange(item.id, columna.id, e.target.value)}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Función para agregar al switch de renderField en FormularioEditor
 * Agregar estos casos en la función renderField del FormularioEditor:
 */
export const renderTableFields = (campo, respuestas, handleChange) => {
  switch (campo.tipo) {
    case 'tabla_dinamica':
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {campo.etiqueta}
            {campo.requerido && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <TablaDinamica
            campo={campo}
            value={respuestas[campo.id] || []}
            onChange={(value) => handleChange(campo.id, value)}
          />
        </Box>
      );
      
    case 'tabla_evaluacion':
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {campo.etiqueta}
            {campo.requerido && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <TablaEvaluacion
            campo={campo}
            value={respuestas[campo.id] || {}}
            onChange={(value) => handleChange(campo.id, value)}
          />
        </Box>
      );
      
    default:
      return null;
  }
};

/**
 * Función para agregar al FormularioVisualizador para mostrar las respuestas
 */
export const renderTableViews = (campo, value) => {
  switch (campo.tipo) {
    case 'tabla_dinamica':
      if (!value || !Array.isArray(value) || value.length === 0) {
        return <Typography variant="body2" color="textSecondary">No hay datos registrados</Typography>;
      }
      
      return (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {campo.configuracion.columnas.map((col) => (
                  <TableCell key={col.id} sx={{ fontWeight: 'bold' }}>
                    {col.etiqueta}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {value.map((fila, index) => (
                <TableRow key={index}>
                  {campo.configuracion.columnas.map((col) => (
                    <TableCell key={col.id}>
                      {fila[col.id] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
      
    case 'tabla_evaluacion':
      if (!value || Object.keys(value).length === 0) {
        return <Typography variant="body2" color="textSecondary">No hay evaluaciones registradas</Typography>;
      }
      
      return (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {campo.configuracion.columnas.map((col) => (
                  <TableCell key={col.id} sx={{ fontWeight: 'bold' }}>
                    {col.etiqueta}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {campo.configuracion.areas.map((grupo) => (
                <React.Fragment key={grupo.grupo}>
                  <TableRow>
                    <TableCell 
                      colSpan={campo.configuracion.columnas.length}
                      sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                    >
                      {grupo.grupo}
                    </TableCell>
                  </TableRow>
                  {grupo.items.map((item) => (
                    <TableRow key={item.id}>
                      {campo.configuracion.columnas.map((col) => (
                        <TableCell key={col.id}>
                          {col.solo_lectura ? 
                            item.nombre : 
                            (value[item.id]?.[col.id] || '-')
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
      
    default:
      return null;
  }
};